import React from "react";
import moment from "moment";
import { LineChart } from "@/components/dataviz/LineChartV2";

// The aggr endpoint emits `timeInterval` per bucket as:
//   month → "2026-01", week → "2026-W07", day → "2026-01-15".
// Pretty-print those for the x-axis (matches the v1 "Jan / Feb …" labels). The
// raw value is still what LineChart uses for tick selection + the data domain —
// this only changes the displayed label.
const X_TICK_FORMATTERS = {
  month: (v) => { const m = moment(String(v), "YYYY-MM", true); return m.isValid() ? m.format("MMM") : String(v); },
  week: (v) => { const m = moment(String(v), "GGGG-[W]WW"); return m.isValid() ? m.format("MMM D") : String(v); },
  day: (v) => { const m = moment(String(v), "YYYY-MM-DD", true); return m.isValid() ? m.format("MMM D") : String(v); },
};
const xTickFormatterFor = (interval) => X_TICK_FORMATTERS[interval] || X_TICK_FORMATTERS.month;

/**
 * Reusable LensesV2 over-time multi-line chart — a thin wrapper over the shared
 * dataviz LineChart with the dashboard's defaults. Takes the
 * aggrMetricsAcrossTimeAndDimension shape DIRECTLY:
 *   [{ timeInterval, dimensions: [{ value, display, count }] }]
 * one line per `dimension` (LineChart pivots internally). Filter `dimensions` to
 * the entities you want before passing (e.g. narrowToSignals). Reuse anywhere a
 * per-entity trend-over-time line is needed (Signals, Topics, …).
 *
 * @param {Array}   data       over-time buckets (timeInterval + dimensions)
 * @param {Object}  [colors]   { display: color } overrides; else auto palette
 * @param {number}  [colorIdx] auto-palette offset when no `colors`
 * @param {number}  [height]
 * @param {string}  [interval] period bucket size (month/week/day) — drives x-axis tick spacing AND label format
 * @param {string}  [highlight] a line's `display` to emphasise (others dim)
 * @param {number}  [defaultOpacity] resting line opacity (default 0.4 = washed-out; hovering a line pops it to full and dims the rest — built into LineChart)
 */
export const TrendLineChart = ({ data, colors, colorIdx = 0, height = 240, interval, highlight = null, defaultOpacity = 0.4, style, ...rest }) => {
  if (!data || data.length < 2) return null;
  return (
    <LineChart
      data={data}
      xAxisPath="timeInterval"
      height={height}
      hideYAxis
      hideXAxisLine
      smartTicks
      responsive
      interval={interval}
      xAxisTickFormatter={xTickFormatterFor(interval)}
      colorIdx={colorIdx}
      {...(colors ? { colors } : {})}
      {...(highlight ? { highlightLine: highlight } : {})}
      defaultOpacity={defaultOpacity}
      margin={{ top: 10, right: 10, bottom: 30, left: 10 }}
      style={{ minHeight: height, height, ...style }}
      {...rest}
    />
  );
};
